/* eslint-disable react/destructuring-assignment */
import React from 'react';
import { Avatar, SkeletonLoading } from '@saleshandy/design-system';
import {
  Alarm,
  AlertCircle,
  CircleCheck,
  ForwardStep,
  Link as LinkIcon,
  Check,
} from '@saleshandy/icons';
import { Link } from 'react-router-dom';
import RetryIcon from '../../../../../shared/svg/retry-icon';
import { Column } from '../../../../../shared/design-system/components/organisms/table/types';
import { getStepPriority } from '../../../../sequence/helpers/sequence-step';
import { accessibleOnClick } from '../../../../../shared/utils/accessible-on-click';
import {
  OverlayTooltip,
  Placement,
} from '../../../../../shared/design-system/components/overlay';
import { TaskAction, TaskStatusKey } from '../../../utils/enums';
import { FAILED_REASON_LABELS } from '../../../../linkedin-automation/types';
import ContactNameField from '../../../../../shared/design-system/components/molecules/contact-name-field';
import {
  SequenceStepType,
  SortOrder,
  SequenceProgress,
} from '../../../../sequence/enums';
import {
  getDateColumnTitle,
  getSortByKeyFromTaskStatus,
  getTaskDate,
  isActionsVisible,
  SELECT_ALL_PRODUCTS_LIMIT,
  TaskSortBy,
} from './helper';
import { Priority } from '../../../../../shared/components/priority';
import { getFormattedDateWithMoment } from '../../../../../shared/utils';
import {
  getOptionFromOutcome,
  mapOutcomeToOption,
} from '../../../../sequence/components/sequence-single-content/components/sequence-single-contacts/components/prospect-outcome-select/utils/helper';
import { getOutcomeIcon } from '../../../../sequence/components/sequence-single-content/components/sequence-single-contacts/components/contact-list-content/components/helpers/step-filter-mapper';
import GenericCustomOutcomeDropdown from '../../../../settings/components/custom-outcomes/components/custom-outcome-dropdown';
import Checkbox from '../../../../../shared/design-system/components/atoms/checkbox';
import { Outcome } from '../../../../settings/types/outcome';
import { getStepIcon } from './step-icon';
import AutomatedBadge from '../components/automated-task-badge';

const copyTaskLink = (
  hashId: string,
  taskStatus: TaskStatusKey,
  rowId: number,
  setCopiedRows: React.Dispatch<React.SetStateAction<Set<number>>>,
) => {
  const baseUrl = window?.location?.origin;
  const taskLink = `${baseUrl}/tasks?status=${taskStatus}&taskId=${hashId}`;
  navigator?.clipboard?.writeText(taskLink);

  setCopiedRows((prev) => new Set(Array.from(prev).concat(rowId)));
  setTimeout(() => {
    setCopiedRows((prev) => {
      const newSet = new Set(prev);
      newSet.delete(rowId);
      return newSet;
    });
  }, 2000);
};

const actions = [
  {
    icon: <Alarm width={20} height={20} className="gray-txt-15" />,
    tooltipText: 'Snooze Task',
    taskAction: TaskAction.SnoozeTask,
  },
  {
    icon: <ForwardStep width={20} height={20} className="gray-txt-15" />,
    tooltipText: 'Skip Task',
    taskAction: TaskAction.SkipTask,
  },
  {
    icon: <CircleCheck width={20} height={20} className="gray-txt-15" />,
    tooltipText: 'Complete Task',
    taskAction: TaskAction.ExecuteTask,
  },
  {
    icon: <LinkIcon width={20} height={20} className="gray-txt-15" />,
    tooltipText: 'Copy Task Link',
    taskAction: 'copyLink',
  },
];

const getTaskIcon = (type: SequenceStepType) =>
  getStepIcon({ type, width: 20, height: 20, module: 'tasks-columns' });
const getDateColumnCellValue = (taskStatus: TaskStatusKey, date: string) => {
  if (taskStatus === TaskStatusKey.DueToday) {
    return 'Today';
  }
  if (taskStatus === TaskStatusKey.Upcoming) {
    return (
      <span
        className={`regular-2 ${
          taskStatus === TaskStatusKey.Upcoming
            ? 'popover-arrow-color-txt'
            : 'red-txt-12'
        }`}
      >{`Due in ${getFormattedDateWithMoment(date)}`}</span>
    );
  }
  if (taskStatus === TaskStatusKey.Overdue) {
    return (
      <span className="regular-2 red-txt-12">{`Due ${getFormattedDateWithMoment(
        date,
      )} ago`}</span>
    );
  }

  return (
    <span
      className={`regular-2 ${
        taskStatus === TaskStatusKey.Completed
          ? 'popover-arrow-color-txt'
          : 'orange-txt-14'
      }`}
    >
      {getFormattedDateWithMoment(date)}{' '}
      {getFormattedDateWithMoment(date)?.toLowerCase() !== 'today' && 'ago'}
    </span>
  );
};

export const getTasksColumns = (
  taskStatus: TaskStatusKey,
  updateTasks: (
    id: number,
    taskAction: TaskAction,
    taskType: SequenceStepType,
  ) => void,
  onTaskClick: (id: number) => void,
  onSort?: (field: TaskSortBy, order: SortOrder) => void,
  bulkSelected?: any[],
  copiedRows?: Set<number>,
  setCopiedRows?: React.Dispatch<React.SetStateAction<Set<number>>>,
  onProspectClick?: (prospectId: number) => void,
  isFirstTimeUser?: boolean,
  allOutcomes?: Outcome[],
  options?: {
    selectedTasks?: any[];
    bulkSelectedTasks?: any;
    singleSelected?: (row: any, status: boolean) => void;
    allSelected?: (rows: any[], status: boolean) => void;
    tasksData?: any[];
    pageNumber?: number;
    pageLimit?: number;
  },
  hoveredRowId?: number | null,
  onRowMouseEnter?: (rowId: number) => void,
  onRowMouseLeave?: () => void,
  hoveredRowRef?: React.MutableRefObject<number | null>,
  onOutcomeChange?: (payload: {
    sequenceId: number;
    prospectId: number;
    outcomeId: number;
  }) => void,
  onFailedReasonClick?: (task: any) => void,
  onRetry?: (taskId: number) => void,
): Column[] => {
  const columns: Column[] = [];
  if (
    taskStatus !== TaskStatusKey.Completed &&
    taskStatus !== TaskStatusKey.Skipped
  ) {
    columns.push({
      dataField: 'customCheckbox',
      text: '',
      headerAlign: 'left',
      headerClasses: 'task-checkbox-header',
      cellClasses: 'task-checkbox-field',
      headerComponent: () => {
        if (!options) return <span />;
        const {
          selectedTasks = [],
          bulkSelectedTasks,
          allSelected,
          tasksData = [],
        } = options;
        const allChecked =
          tasksData.length > 0 &&
          tasksData?.every((row, rowIndex) => {
            if (
              bulkSelectedTasks?.isAllTasksSelected &&
              options?.pageNumber &&
              options?.pageLimit
            ) {
              const globalIndex =
                (options?.pageNumber - 1) * options?.pageLimit + rowIndex;
              if (globalIndex >= SELECT_ALL_PRODUCTS_LIMIT) return false;
              return !(bulkSelectedTasks?.deselectedTaskIds || [])?.includes(
                row?.id,
              );
            }
            return selectedTasks?.some((sel) => sel.id === row.id);
          });
        const someChecked = tasksData?.some((row, rowIndex) => {
          if (
            bulkSelectedTasks?.isAllTasksSelected &&
            options?.pageNumber &&
            options?.pageLimit
          ) {
            const globalIndex =
              (options?.pageNumber - 1) * options?.pageLimit + rowIndex;
            if (globalIndex >= SELECT_ALL_PRODUCTS_LIMIT) return false;
            return !(bulkSelectedTasks?.deselectedTaskIds || [])?.includes(
              row?.id,
            );
          }
          return selectedTasks?.some((sel) => sel.id === row?.id);
        });
        return (
          <div className="d-flex align-items-center">
            <Checkbox
              checked={allChecked}
              ref={(el) => {
                if (el) el.indeterminate = !allChecked && someChecked;
              }}
              onChange={(checked: boolean) =>
                allSelected && allSelected(tasksData, checked)
              }
            />
          </div>
        );
      },
      component: (_cell, row, rowIndex) => {
        const { selectedTasks = [], bulkSelectedTasks, singleSelected } =
          options || {};
        let checked = false;
        if (
          bulkSelectedTasks?.isAllTasksSelected &&
          options?.pageNumber &&
          options?.pageLimit
        ) {
          const globalIndex =
            (options?.pageNumber - 1) * options?.pageLimit + rowIndex;
          if (globalIndex >= SELECT_ALL_PRODUCTS_LIMIT) {
            checked = false;
          } else {
            checked = !(bulkSelectedTasks?.deselectedTaskIds || [])?.includes(
              row?.id,
            );
          }
        } else {
          checked = selectedTasks?.some((sel) => sel?.id === row?.id);
        }
        const onCheckboxChange = (checked1: boolean) => {
          if (singleSelected) singleSelected(row, checked1);
        };
        return (
          <div
            onMouseEnter={() => onRowMouseEnter?.(row.id)}
            onMouseLeave={onRowMouseLeave}
          >
            <Checkbox
              checked={checked}
              onChange={onCheckboxChange}
              {...accessibleOnClick((e) => e?.stopPropagation())}
            />
          </div>
        );
      },
      headerLoadingSkeleton: (
        <SkeletonLoading
          width={16}
          height={16}
          enableAnimation={!isFirstTimeUser}
        />
      ),
      cellLoadingSkeleton: (
        <SkeletonLoading
          width={16}
          height={16}
          enableAnimation={!isFirstTimeUser}
        />
      ),
    });
  }
  columns.push(
    {
      dataField: 'taskTitle',
      text: 'Task Title',
      headerAlign: 'left',
      cellClasses: 'name-field',
      component: (cell, row) => {
        const isCopied = copiedRows?.has(row?.id);
        const isRowHovered = hoveredRowRef?.current === row?.id;
        const isAutomatedLinkedIn =
          cell?.type === SequenceStepType.AimfoxCampaignPush ||
          cell?.type === SequenceStepType.HeyReachCampaignPush;
        const getActionsForRow = () =>
          actions
            ?.filter(({ taskAction }) => {
              if (isAutomatedLinkedIn) {
                return (
                  taskAction === TaskAction.SkipTask ||
                  taskAction === 'copyLink'
                );
              }
              return true;
            })
            ?.map(({ icon, tooltipText, taskAction }) => {
              if (taskAction === 'copyLink' && isCopied) {
                return {
                  icon: (
                    <Check width={16} height={16} className="gray-txt-15" />
                  ),
                  tooltipText: 'Link Copied',
                  taskAction: 'copyLink',
                };
              }
              return { icon, tooltipText, taskAction };
            });
        const isDraftSequence =
          row?.sequence?.progress === SequenceProgress.Draft;
        return (
          <div
            className={`d-flex align-items-center test-task-title${
              isAutomatedLinkedIn
                ? ' li-auto-task-title'
                : ' justify-content-between'
            }`}
            {...accessibleOnClick(() => onTaskClick(row?.id))}
            onMouseEnter={() => onRowMouseEnter?.(row?.id)}
            onMouseLeave={onRowMouseLeave}
          >
            <div className="task-title-wrapper">
              {getTaskIcon(cell?.type)}
              <OverlayTooltip text={cell?.title} placement={Placement.Bottom}>
                <div className="task-title-text-container">
                  <span className="semibold-2 popover-arrow-color-txt ml-1 task-title-col">
                    {cell?.title}
                  </span>
                </div>
              </OverlayTooltip>
              {isDraftSequence &&
                isAutomatedLinkedIn &&
                taskStatus !== TaskStatusKey.Completed && (
                  <OverlayTooltip
                    text="This task belongs to a draft sequence. Task will run only after sequence is activated."
                    placement={Placement.Bottom}
                  >
                    <span className="task-list-item-lock-icon ml-1">
                      <svg
                        width="14"
                        height="17"
                        viewBox="0 0 16 20"
                        fill="none"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M3 9.99251C2.44772 9.99251 2 10.4402 2 10.9925V16.9925C2 17.5448 2.44772 17.9925 3 17.9925H13C13.5523 17.9925 14 17.5448 14 16.9925V10.9925C14 10.4402 13.5523 9.99251 13 9.99251H3ZM0 10.9925C0 9.33566 1.34315 7.99251 3 7.99251H13C14.6569 7.99251 16 9.33566 16 10.9925V16.9925C16 18.6494 14.6569 19.9925 13 19.9925H3C1.34315 19.9925 0 18.6494 0 16.9925V10.9925Z"
                          fill="currentColor"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M6 14C6 12.8954 6.89543 12 8 12C9.10457 12 10 12.8954 10 14C10 15.1046 9.10457 16 8 16C6.89543 16 6 15.1046 6 14Z"
                          fill="currentColor"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M8 2C7.20435 2 6.44129 2.31607 5.87868 2.87868C5.31607 3.44129 5 4.20435 5 5V9C5 9.55229 4.55228 10 4 10C3.44772 10 3 9.55229 3 9V5C3 3.67392 3.52678 2.40215 4.46447 1.46447C5.40215 0.526784 6.67392 0 8 0C9.32608 0 10.5979 0.526784 11.5355 1.46447C12.4732 2.40215 13 3.67392 13 5V9C13 9.55229 12.5523 10 12 10C11.4477 10 11 9.55229 11 9V5C11 4.20435 10.6839 3.44129 10.1213 2.87868C9.55871 2.31607 8.79565 2 8 2Z"
                          fill="currentColor"
                        />
                      </svg>
                    </span>
                  </OverlayTooltip>
                )}
            </div>
            {taskStatus === TaskStatusKey.Failed &&
              isAutomatedLinkedIn &&
              isRowHovered && (
                <div className="action-container action-container--compact show-actions">
                  <OverlayTooltip text="Retry" placement={Placement.Bottom}>
                    <div
                      className="action-item"
                      {...accessibleOnClick((e) => {
                        e.stopPropagation();
                        updateTasks(
                          row?.id,
                          TaskAction.RetryLinkedInTask,
                          row?.taskTitle?.type,
                        );
                      })}
                    >
                      <RetryIcon width={13} height={13} className="gray-txt-15" />
                    </div>
                  </OverlayTooltip>
                  <OverlayTooltip text="Skip Task" placement={Placement.Bottom}>
                    <div
                      className="action-item"
                      {...accessibleOnClick((e) => {
                        e.stopPropagation();
                        updateTasks(
                          row?.id,
                          TaskAction.SkipTask,
                          row?.taskTitle?.type,
                        );
                      })}
                    >
                      <ForwardStep
                        width={20}
                        height={20}
                        className="gray-txt-15"
                      />
                    </div>
                  </OverlayTooltip>
                </div>
              )}
            {!isActionsVisible(taskStatus) && !(bulkSelected?.length > 1) && (
              <div
                className={`action-container ${
                  isRowHovered ? 'show-actions' : ''
                }${isAutomatedLinkedIn ? ' action-container--compact' : ''}`}
              >
                {getActionsForRow()?.map(
                  ({ icon, tooltipText, taskAction }) => (
                    <OverlayTooltip text={tooltipText} key={tooltipText}>
                      <div
                        className="action-item"
                        {...accessibleOnClick((e) => {
                          e.stopPropagation();
                          if (taskAction === 'copyLink') {
                            if (setCopiedRows) {
                              copyTaskLink(
                                row?.hashId,
                                taskStatus,
                                row?.id,
                                setCopiedRows,
                              );
                            }
                          } else {
                            updateTasks(
                              row?.id,
                              taskAction as TaskAction,
                              row?.taskTitle?.type,
                            );
                          }
                        })}
                      >
                        {icon}
                      </div>
                    </OverlayTooltip>
                  ),
                )}
              </div>
            )}
          </div>
        );
      },
      headerLoadingSkeleton: (
        <SkeletonLoading
          width={120}
          height={14}
          enableAnimation={!isFirstTimeUser}
        />
      ),
      cellLoadingSkeleton: (
        <div>
          <SkeletonLoading
            width={72}
            height={14}
            enableAnimation={!isFirstTimeUser}
          />
          <SkeletonLoading
            width={160}
            height={8}
            enableAnimation={!isFirstTimeUser}
          />
        </div>
      ),
    },
    {
      dataField: 'prospect',
      text: 'Prospect',
      headerAlign: 'left',
      cellClasses: 'prospect-field prospect-task-col',
      headerClasses: 'name-field',
      component: (cell, row) => (
        <div
          className="pointer"
          onMouseEnter={() => onRowMouseEnter?.(row?.id)}
          onMouseLeave={onRowMouseLeave}
        >
          <ContactNameField
            cell={cell}
            status={row.status}
            onClick={(prospectId) => onProspectClick(prospectId)}
            maxEmailLength={50}
            showProspectDetailsTooltipOnEmail={true}
          />
        </div>
      ),
      headerLoadingSkeleton: (
        <SkeletonLoading
          width={120}
          height={14}
          enableAnimation={!isFirstTimeUser}
        />
      ),
      cellLoadingSkeleton: (
        <div>
          <SkeletonLoading
            width={72}
            height={14}
            enableAnimation={!isFirstTimeUser}
          />
          <SkeletonLoading
            width={160}
            height={8}
            enableAnimation={!isFirstTimeUser}
          />
        </div>
      ),
    },
    {
      dataField: 'sequence',
      text: 'Source',
      headerAlign: 'left',
      cellClasses: 'name-field sequence-task-col',
      component: (cell, row) => {
        const isSubsequence = cell?.sequenceType === 'subsequence';
        const toUrl = isSubsequence
          ? `/sequence/${cell?.parentSequenceId}/subsequence/${cell?.sequenceId}/steps`
          : `/sequence/${cell?.sequenceId}/steps`;
        return (
          <div
            className="d-flex align-items-center"
            onMouseEnter={() => onRowMouseEnter?.(row?.id)}
            onMouseLeave={onRowMouseLeave}
          >
            <span className="regular-2 popover-arrow-color-txt">
              {cell?.stepNumber && `Step ${cell?.stepNumber}`}
            </span>

            {cell?.stepNumber ? (
              <Link
                className="ml-1 regular-2 font-medium blue-txt-11 pointer"
                to={toUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <OverlayTooltip
                  text={cell.sequenceName}
                  placement={Placement.Bottom}
                >
                  <div className="sequence-name-text-container w-100">
                    <span className="sequence-name-col">
                      {cell?.sequenceName}
                    </span>
                  </div>
                </OverlayTooltip>
              </Link>
            ) : (
              <span className="manual-task-creation">Manual Task</span>
            )}
          </div>
        );
      },
      headerLoadingSkeleton: (
        <SkeletonLoading
          width={120}
          height={14}
          enableAnimation={!isFirstTimeUser}
        />
      ),
      cellLoadingSkeleton: (
        <div>
          <SkeletonLoading
            width={72}
            height={14}
            enableAnimation={!isFirstTimeUser}
          />
          <SkeletonLoading
            width={160}
            height={8}
            enableAnimation={!isFirstTimeUser}
          />
        </div>
      ),
    },
  );

  if (taskStatus !== TaskStatusKey.Failed) {
    columns.push({
      dataField: 'priority',
      text: 'Priority',
      headerAlign: 'left',
      headerClasses: 'task-sorting',
      cellClasses: 'tag-field',
      sort: true,
      onSort: (_field, order: SortOrder) => onSort(TaskSortBy.Priority, order),
      component: (cell, row) => {
        const { text, variant, className } = getStepPriority(cell);

        return (
          <div
            className="d-flex white-space-nowrap"
            onMouseEnter={() => onRowMouseEnter?.(row?.id)}
            onMouseLeave={onRowMouseLeave}
          >
            <Priority text={text} variant={variant} className={className} />
          </div>
        );
      },
      headerLoadingSkeleton: (
        <SkeletonLoading
          width={100}
          height={14}
          enableAnimation={!isFirstTimeUser}
        />
      ),
      cellLoadingSkeleton: (
        <div>
          <SkeletonLoading
            width={72}
            height={14}
            enableAnimation={!isFirstTimeUser}
          />
          <SkeletonLoading
            width={120}
            height={8}
            enableAnimation={!isFirstTimeUser}
          />
        </div>
      ),
    } as any);
  }

  if (taskStatus !== TaskStatusKey.Failed) {
    columns.push({
      dataField: 'date',
      text: getDateColumnTitle(taskStatus),
      headerAlign: 'left',
      headerClasses: 'task-sorting',
      cellClasses: 'name-field',
      sort: true,
      onSort: (_field, order: SortOrder) =>
        onSort(getSortByKeyFromTaskStatus(taskStatus), order),
      component: (cell, row) => {
        const date = new Date(getTaskDate(taskStatus, cell));
        return (
          <div
            className="d-flex white-space-nowrap"
            onMouseEnter={() => onRowMouseEnter?.(row?.id)}
            onMouseLeave={onRowMouseLeave}
          >
            <OverlayTooltip text={date.toLocaleString()}>
              <span className="regular-2 popover-arrow-color-txt">
                {getDateColumnCellValue(
                  taskStatus,
                  getTaskDate(taskStatus, cell),
                )}
              </span>
            </OverlayTooltip>
          </div>
        );
      },
      headerLoadingSkeleton: (
        <SkeletonLoading
          width={120}
          height={14}
          enableAnimation={!isFirstTimeUser}
        />
      ),
      cellLoadingSkeleton: (
        <div>
          <SkeletonLoading
            width={72}
            height={14}
            enableAnimation={!isFirstTimeUser}
          />
          <SkeletonLoading
            width={130}
            height={8}
            enableAnimation={!isFirstTimeUser}
          />
        </div>
      ),
    } as any);
  } else {
    columns.push({
      dataField: 'date',
      text: 'Failed At',
      headerAlign: 'left',
      cellClasses: 'name-field',
      component: (cell, row) => {
        const rawDate = cell?.completedAt;
        const formatted = rawDate ? getFormattedDateWithMoment(rawDate) : '—';
        const display =
          formatted === '—' || formatted.toLowerCase() === 'today'
            ? formatted
            : `${formatted} ago`;
        return (
          <div
            className="d-flex white-space-nowrap"
            onMouseEnter={() => onRowMouseEnter?.(row?.id)}
            onMouseLeave={onRowMouseLeave}
          >
            <span className="regular-2 gray-txt-10">{display}</span>
          </div>
        );
      },
      headerLoadingSkeleton: (
        <SkeletonLoading
          width={100}
          height={14}
          enableAnimation={!isFirstTimeUser}
        />
      ),
      cellLoadingSkeleton: (
        <SkeletonLoading
          width={90}
          height={14}
          enableAnimation={!isFirstTimeUser}
        />
      ),
    } as any);
  }

  if (taskStatus !== TaskStatusKey.Failed) {
    columns.push({
      dataField: 'prospectOutcome',
      text: 'Prospect Outcome',
      headerAlign: 'left',
      cellClasses: 'prospect-outcome-col',
      component: (cell, row) => {
        // For manual tasks
        if (!row?.sequence?.stepNumber) {
          return (
            <div
              className="d-flex align-items-center"
              onMouseEnter={() => onRowMouseEnter?.(row?.id)}
              onMouseLeave={onRowMouseLeave}
            >
              <span className="regular-2 gray-txt-10">-</span>
            </div>
          );
        }
        const initialOption = getOptionFromOutcome(
          allOutcomes,
          row?.prospectOutcome?.categoryId,
        );
        return (
          <div
            className="d-flex align-items-center"
            onMouseEnter={() => onRowMouseEnter?.(row?.id)}
            onMouseLeave={onRowMouseLeave}
          >
            <GenericCustomOutcomeDropdown
              showFooter={false}
              options={mapOutcomeToOption(allOutcomes)}
              showTotal={false}
              className="prospect-outcome-select"
              changeOutcomeRequestPayload={{
                sequenceId: row?.sequence?.sequenceId,
                prospectId: row?.prospect?.[0]?.id,
              }}
              initialSelectedOption={initialOption}
              onChange={(option) => {
                if (option && onOutcomeChange) {
                  onOutcomeChange({
                    sequenceId: row?.sequence?.sequenceId,
                    prospectId: row?.prospect?.[0]?.id,
                    outcomeId: option?.id,
                  });
                }
              }}
              optionRenderer={(option) => (
                <div className="d-flex align-items-center">
                  {getOutcomeIcon(option?.name, false, {
                    isDefault: option.isDefault,
                    sentiment: option.sentiment,
                  })}
                  <span className="ml-1">{option.name}</span>
                </div>
              )}
              delayUpdate={true}
            />
          </div>
        );
      },
      headerLoadingSkeleton: (
        <SkeletonLoading
          width={120}
          height={14}
          enableAnimation={!isFirstTimeUser}
        />
      ),
      cellLoadingSkeleton: (
        <div>
          <SkeletonLoading
            width={72}
            height={14}
            enableAnimation={!isFirstTimeUser}
          />
          <SkeletonLoading
            width={160}
            height={8}
            enableAnimation={!isFirstTimeUser}
          />
        </div>
      ),
    } as any);
  }

  if (taskStatus !== TaskStatusKey.Failed) {
    columns.push({
      dataField: 'owner',
      text: 'Task Owner',
      headerAlign: 'left',
      cellClasses: 'owner',
      component: (cell, row) => {
        const ownerName = `${cell.firstName} ${cell.lastName}`;
        return (
          <div
            className="d-flex align-items-center"
            onMouseEnter={() => onRowMouseEnter?.(row?.id)}
            onMouseLeave={onRowMouseLeave}
          >
            <Avatar firstName={cell.firstName} lastName={cell.lastName} />
            <div className="ml-2 owner-name" style={{ flex: 1, minWidth: 0 }}>
              <OverlayTooltip text={ownerName} placement={Placement.Bottom}>
                <div className="owner-name-text-container">
                  <span className="owner-name-col regular-2 gray-txt-10">
                    {ownerName}
                  </span>
                </div>
              </OverlayTooltip>
            </div>
          </div>
        );
      },
      headerLoadingSkeleton: (
        <SkeletonLoading
          width={100}
          height={14}
          enableAnimation={!isFirstTimeUser}
        />
      ),
      cellLoadingSkeleton: (
        <div>
          <SkeletonLoading
            width={72}
            height={14}
            enableAnimation={!isFirstTimeUser}
          />
          <SkeletonLoading
            width={110}
            height={8}
            enableAnimation={!isFirstTimeUser}
          />
        </div>
      ),
    } as any);
  }

  if (taskStatus === TaskStatusKey.Failed) {
    columns.push({
      dataField: 'failedReason',
      text: 'Failed Reason',
      headerAlign: 'left',
      cellClasses: 'name-field',
      component: (_cell, row) => {
        const label =
          FAILED_REASON_LABELS[row?.failedReason] || 'Unknown Error';
        return (
          <div
            className="d-flex align-items-center"
            onMouseEnter={() => onRowMouseEnter?.(row?.id)}
            onMouseLeave={onRowMouseLeave}
          >
            <button
              type="button"
              className="failed-reason-column__btn btn btn-link p-0 d-flex align-items-center regular-2 red-txt-12"
              {...accessibleOnClick((e) => {
                e.stopPropagation();
                if (onFailedReasonClick) onFailedReasonClick(row);
              })}
            >
              <AlertCircle className="failed-reason-column__icon" />
              {label}
            </button>
            {onRetry && hoveredRowRef?.current === row?.id && (
              <OverlayTooltip text="Retry Task" placement={Placement.Bottom}>
                <button
                  type="button"
                  className="failed-reason-column__retry-btn btn btn-link p-0 ml-2 regular-2 gray-txt-11"
                  aria-label="Retry task"
                  {...accessibleOnClick((e) => {
                    e.stopPropagation();
                    onRetry(row?.id);
                  })}
                >
                  <RetryIcon />
                </button>
              </OverlayTooltip>
            )}
          </div>
        );
      },
      headerLoadingSkeleton: (
        <SkeletonLoading
          width={120}
          height={14}
          enableAnimation={!isFirstTimeUser}
        />
      ),
      cellLoadingSkeleton: (
        <SkeletonLoading
          width={100}
          height={14}
          enableAnimation={!isFirstTimeUser}
        />
      ),
    });

    // Task Owner after Failed Reason
    columns.push({
      dataField: 'owner',
      text: 'Task Owner',
      headerAlign: 'left',
      cellClasses: 'owner',
      component: (cell, row) => {
        const ownerName = `${cell?.firstName || ''} ${
          cell?.lastName || ''
        }`.trim();
        return (
          <div
            className="d-flex align-items-center"
            onMouseEnter={() => onRowMouseEnter?.(row?.id)}
            onMouseLeave={onRowMouseLeave}
          >
            <Avatar firstName={cell?.firstName} lastName={cell?.lastName} />
            <div className="ml-2 owner-name" style={{ flex: 1, minWidth: 0 }}>
              <OverlayTooltip text={ownerName} placement={Placement.Bottom}>
                <div className="owner-name-text-container">
                  <span className="owner-name-col regular-2 gray-txt-10">
                    {ownerName}
                  </span>
                </div>
              </OverlayTooltip>
            </div>
          </div>
        );
      },
      headerLoadingSkeleton: (
        <SkeletonLoading
          width={100}
          height={14}
          enableAnimation={!isFirstTimeUser}
        />
      ),
      cellLoadingSkeleton: (
        <div>
          <SkeletonLoading
            width={72}
            height={14}
            enableAnimation={!isFirstTimeUser}
          />
          <SkeletonLoading
            width={110}
            height={8}
            enableAnimation={!isFirstTimeUser}
          />
        </div>
      ),
    });
  }

  return columns;
};
